import Button from "components/Button";
import { Grid } from "components/Grid/Grid";
import Loader from "components/Loader";
import SingleSelect from "components/SingleSelect";
import Text from "components/Text";
import TextInput from "components/TextInput";
import { format } from "date-fns";
import PageContent from "pages/PageContent";
import {
    ProfileContentWrapper,
    StraightLine,
    SubHeading,
} from "pages/PageContent/PageContent.styled";
import React from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { BsArrowRight } from "react-icons/bs";
import { RiDeleteBin5Line } from "react-icons/ri";
import theme from "theme";
import { ImageUpload } from "./ImageUpload/ImageUpload";
import { ButtonContainer, CircularButton } from "./PersonalDetails.styled";
import { usePersonalDetails } from "./usePersonalDetails";

export const PersonalDetails: React.FC = React.memo(() => {
    const {
        personalDetails,
        personalDetailsErrors,
        socialHandles,
        handleInputChange,
        addSocialHandle,
        removeSocialHandle,
        onPersonalDetailsSubmit,
        handleSelectChange,
        socialPlatformsOptions,
        handleSocialUsername,
        handleSocialSelect,
        handleSocialLinksSubmit,
        isSocialLoading,
        isSocialFetching,
        isPersonalDetailLoading,
        isPersonalDetailsFetching,
        clickedOnNext,
        handleSaveAndNext,
        nationalityData,
    } = usePersonalDetails();

    return (
        <PageContent>
            <ProfileContentWrapper>
                {(isSocialFetching || isPersonalDetailsFetching) && (
                    <Loader size={28} />
                )}
                <Text
                    variant="heading5"
                    fontWeight="700"
                    color={theme.colors.grey.dark}
                >
                    Complete Your Profile (1/7)
                </Text>
                <SubHeading variant="label">
                    PERSONAL DETAILS <StraightLine />
                </SubHeading>
                {(!isSocialFetching || !isPersonalDetailsFetching) && (
                    <>
                        <ImageUpload />
                        <Grid
                            row={true}
                            expanded
                            marginTop={2}
                            columnGap={1}
                            rowGap={2}
                        >
                            <TextInput
                                name="first_name"
                                type="text"
                                label="First Name"
                                required
                                placeholder="Enter First Name"
                                labelColor={theme.colors.text}
                                minWidth="160px"
                                value={personalDetails.first_name}
                                errorMsg={personalDetailsErrors.first_name}
                                onChange={handleInputChange}
                            />
                            <TextInput
                                name="last_name"
                                type="text"
                                label="Last Name"
                                required
                                placeholder="Enter Last Name"
                                labelColor={theme.colors.text}
                                minWidth="160px"
                                value={personalDetails.last_name}
                                errorMsg={personalDetailsErrors.last_name}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid
                            row={true}
                            expanded
                            marginTop={2}
                            columnGap={1}
                            rowGap={2}
                        >
                            <SingleSelect
                                label="Gender"
                                name="gender"
                                required
                                minWidth="160px"
                                options={[
                                    {
                                        value: "male",
                                        label: "Male",
                                    },
                                    {
                                        value: "female",
                                        label: "Female",
                                    },
                                    {
                                        value: "other",
                                        label: "Other",
                                    },
                                ]}
                                value={personalDetails.gender}
                                onChange={(value) =>
                                    handleSelectChange("gender", value)
                                }
                                errorText={personalDetailsErrors.gender}
                            />
                            <SingleSelect
                                label="Nationality"
                                required
                                minWidth="200px"
                                name="nationality"
                                searchable
                                options={nationalityData}
                                value={personalDetails.nationality}
                                errorText={personalDetailsErrors.nationality}
                                onChange={(value) =>
                                    handleSelectChange("nationality", value)
                                }
                            />
                            <TextInput
                                name="dob"
                                type="date"
                                label="Date of Birth"
                                min="1950-01-01"
                                max={format(new Date(), "yyyy-MM-dd")}
                                required
                                placeholder="Enter Date of Birth"
                                labelColor={theme.colors.text}
                                minWidth="160px"
                                value={personalDetails.dob}
                                onChange={handleInputChange}
                                errorMsg={personalDetailsErrors.dob}
                            />
                            <SingleSelect
                                label="Qualification"
                                required
                                minWidth="160px"
                                name="graduation"
                                options={[
                                    {
                                        value: "Graduation",
                                        label: "Graduation",
                                    },
                                    {
                                        value: "Post Graduation",
                                        label: "Post Graduation",
                                    },
                                ]}
                                value={personalDetails.qualification}
                                onChange={(value) =>
                                    handleSelectChange("qualification", value)
                                }
                                errorText={personalDetailsErrors.qualification}
                            />
                        </Grid>
                        <Grid row={true} expanded marginTop={2} columnGap={1}>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={onPersonalDetailsSubmit}
                                disabled={isPersonalDetailLoading}
                                isLoading={
                                    !clickedOnNext && isPersonalDetailLoading
                                }
                            >
                                Save Personal Details
                            </Button>
                        </Grid>
                        <SubHeading variant="label">
                            SOCIAL MEDIA <StraightLine />
                        </SubHeading>
                        {socialHandles.map((socialHandle) => (
                            <Grid
                                row={true}
                                expanded
                                marginTop={2}
                                columnGap={1}
                                rowGap={2}
                                key={socialHandle.id}
                            >
                                <SingleSelect
                                    label="Social"
                                    required
                                    minWidth="160px"
                                    options={socialPlatformsOptions}
                                    value={socialHandle.social_platforms_id}
                                    onChange={(value: any) =>
                                        handleSocialSelect(
                                            value,
                                            socialHandle.id
                                        )
                                    }
                                />
                                <TextInput
                                    name="username"
                                    type="text"
                                    label="Username"
                                    required
                                    placeholder="Enter Username"
                                    labelColor={theme.colors.text}
                                    minWidth="160px"
                                    value={socialHandle.username}
                                    onChange={(event) =>
                                        handleSocialUsername(
                                            event,
                                            socialHandle.id
                                        )
                                    }
                                    errorMsg={socialHandle.error}
                                />
                                <ButtonContainer>
                                    <CircularButton
                                        type="button"
                                        variant="flat"
                                        disabled={socialHandles.length <= 1}
                                        onClick={() =>
                                            removeSocialHandle(socialHandle.id)
                                        }
                                    >
                                        <RiDeleteBin5Line color="red" />
                                    </CircularButton>
                                    <CircularButton
                                        type="button"
                                        variant="flat"
                                        onClick={() => addSocialHandle()}
                                        disabled={
                                            socialPlatformsOptions.length ===
                                            socialHandles.length
                                        }
                                    >
                                        <AiOutlinePlus
                                            color="green"
                                            fontWeight={700}
                                        />
                                    </CircularButton>
                                </ButtonContainer>
                            </Grid>
                        ))}
                        <Grid
                            row={true}
                            expanded
                            marginTop={2}
                            columnGap={1}
                            rowGap={1}
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleSocialLinksSubmit}
                                disabled={isSocialLoading}
                                isLoading={!clickedOnNext && isSocialLoading}
                            >
                                Save Social Link
                            </Button>
                            <Button
                                variant="filled"
                                icon={<BsArrowRight />}
                                iconPosition="suffix"
                                isLoading={clickedOnNext}
                                onClick={handleSaveAndNext}
                            >
                                Save and Next
                            </Button>
                        </Grid>
                    </>
                )}
            </ProfileContentWrapper>
        </PageContent>
    );
});

PersonalDetails.displayName = "PersonalDetails";
